class AcSearchOverlay extends HTMLElement {
  #abortController;
  #requestController;
  #timer = 0;
  #generation = 0;
  #input;
  #form;
  #results;
  #popular;
  #scroll;
  #status;
  #railGeneration = 0;
  #announcementFrame = 0;
  #query = '';
  #cache = new Map();
  #metaTimer = 0;
  #pendingMetaSearch;
  #lastMetaQuery = '';
  #predictiveSearch = true;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#input = this.querySelector('[data-ac-search-input]');
    this.#form = this.querySelector('[data-ac-search-form]');
    this.#results = this.querySelector('[data-ac-search-results]');
    this.#popular = this.querySelector('[data-ac-search-popular]');
    this.#scroll = this.querySelector('[data-ac-search-scroll]');
    this.#status = this.querySelector('[data-ac-search-status]');
    if (!this.#input || !this.#form || !this.#results) return;
    try {
      const features = JSON.parse(document.getElementById('shopify-features')?.textContent || '{}');
      // Shopify can advertise the feature globally while rejecting these store locales with 417.
      const locale = document.documentElement.lang.split('-')[0];
      this.#predictiveSearch = features.predictiveSearch !== false && !['ja', 'ko'].includes(locale);
    } catch { /* A missing feature flag falls back on the API's 417 response. */ }

    const signal = this.#abortController.signal;
    this.#input.addEventListener('input', this.#handleInput, { signal });
    this.#input.addEventListener('compositionstart', () => this.#cancelPendingRequest(), { signal });
    this.#input.addEventListener('compositionend', this.#handleInput, { signal });
    this.#form.addEventListener('submit', this.#handleSubmit, { signal });
    this.#results.addEventListener('click', this.#handleResultClick, { signal, capture: true });
    document.addEventListener('visitorConsentCollected', this.#handleTrackingConsent, { signal });
    document.addEventListener('ac:overlay-close', this.#handleOverlayClose, { signal });
    document.addEventListener('ac:overlay-open', this.#handleOverlayOpen, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#cancelPendingRequest();
    this.#stopRail();
  }

  #handleInput = (event) => {
    if (event.isComposing) return;
    const query = this.#input.value.trim();
    if (query && query === this.#query) {
      window.clearTimeout(this.#timer);
      return;
    }
    this.#cancelPendingRequest();
    if (query.length < 2) {
      this.#showPopular();
      return;
    }

    this.#timer = window.setTimeout(() => this.#search(query), 200);
  };

  #handleSubmit = (event) => {
    const query = this.#input.value.trim();
    if (!query) event.preventDefault();
    // Native submission owns the full results page and its Shopify search event.
    this.#cancelPendingRequest();
    this.#input.blur();
  };

  #handleOverlayClose = (event) => {
    if (event.detail?.panel !== this) return;
    this.#reset();
  };

  #handleOverlayOpen = (event) => {
    if (event.detail?.panel !== this) return;
    this.#resetScroll();
    const template = this.#popular?.querySelector('[data-ac-search-popular-template]');
    template?.replaceWith(template.content.cloneNode(true));
    const generation = ++this.#railGeneration;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (this.isConnected && generation === this.#railGeneration && this.getAttribute('aria-hidden') === 'false') {
        this.#refreshRail();
      }
    }));
  };

  #stopRail() {
    this.#railGeneration += 1;
    this.#popular?.querySelector('ac-product-rail')?.suspend?.();
  }

  #hidePopular() {
    this.#stopRail();
    this.#popular?.setAttribute('hidden', '');
  }

  async #refreshRail() {
    const rail = this.#popular?.querySelector('ac-product-rail');
    if (!rail || this.#popular.hidden || this.getAttribute('aria-hidden') !== 'false') return;
    const generation = ++this.#railGeneration;
    try {
      if (!window.Swiper) await window.AcProductGallery?.ensureSwiper({
        script: this.dataset.swiperScript, stylesheet: this.dataset.swiperStylesheet,
      });
    } catch {
      // The shared list remains natively scrollable when enhancement is unavailable.
    }
    if (!this.isConnected || generation !== this.#railGeneration) return;
    if (this.#popular.hidden || this.getAttribute('aria-hidden') !== 'false') return;
    rail.refresh?.();
  }

  async #search(query) {
    if (query === this.#query) return;
    this.#cancelPendingRequest();
    const generation = this.#generation;
    this.#query = query;
    const cached = this.#cache.get(query);
    if (cached) {
      this.#render(cached);
      this.#queueMetaSearch(query, generation);
      return;
    }

    const controller = new AbortController();
    this.#requestController = controller;

    // Keep the current cards while the next response loads; never flash an empty drawer.
    this.#setLoading(true);
    this.#announce(this.dataset.loadingLabel);

    try {
      const request = () => fetch(this.#searchUrl(query), {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: controller.signal,
      });
      let response = await request();
      if (response.status === 417 && this.#predictiveSearch) {
        this.#predictiveSearch = false;
        response = await request();
      }
      if (!response.ok) throw new Error(`Search request failed with ${response.status}`);

      const html = await response.text();
      if (generation !== this.#generation || query !== this.#input.value.trim()) return;
      this.#render(html);
      this.#cache.set(query, html);
      if (this.#cache.size > 20) this.#cache.delete(this.#cache.keys().next().value);
      this.#queueMetaSearch(query, generation);
    } catch (error) {
      if (error.name === 'AbortError' || generation !== this.#generation) return;
      this.#query = '';
      this.#renderError(query);
    } finally {
      if (generation === this.#generation) {
        this.#requestController = null;
        this.#setLoading(false);
      }
    }
  }

  #searchUrl(query) {
    const url = new URL(this.#predictiveSearch ? this.dataset.predictiveSearchUrl : this.dataset.searchUrl, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', this.dataset.resultsSection || 'ac-search-overlay-results');
    if (this.#predictiveSearch) {
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[options][unavailable_products]', 'hide');
    } else {
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('options[unavailable_products]', 'hide');
    }
    return url;
  }


  #normalizeMetaQuery(query) {
    return query.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
  }

  #queueMetaSearch(query, generation) {
    const key = this.#normalizeMetaQuery(query);
    if (key === this.#lastMetaQuery) return;
    const page = new URL(window.location.href);
    if (/\/search\/?$/.test(page.pathname) && this.#normalizeMetaQuery(page.searchParams.get('q') || '') === key) return;
    this.#pendingMetaSearch = { query, key, generation, deadline: Date.now() + 30000 };
    this.#metaTimer = window.setTimeout(() => this.#tryMetaSearch(), 750);
  }

  #handleResultClick = (event) => {
    if (event.target.closest('a[href]')) this.#tryMetaSearch();
  };

  #handleTrackingConsent = () => {
    if (this.#pendingMetaSearch) this.#tryMetaSearch();
  };

  async #tryMetaSearch() {
    window.clearTimeout(this.#metaTimer);
    const pending = this.#pendingMetaSearch;
    if (!pending || pending.key === this.#lastMetaQuery) return;
    if (!this.isConnected || pending.generation !== this.#generation || pending.query !== this.#query) return;
    if (pending.key !== this.#normalizeMetaQuery(this.#input.value)) return;
    try {
      const privacy = await window.AcCustomerPrivacy?.ready();
      if (this.#pendingMetaSearch !== pending || pending.generation !== this.#generation) return;
      if (!privacy?.marketingAllowed() || !privacy.analyticsProcessingAllowed() || !privacy.saleOfDataAllowed()) return;
      const pixelId = this.dataset.metaSearchPixelId;
      const fbq = window.fbq;
      const initialized = typeof fbq?.getState === 'function' && fbq.getState().pixels?.some(pixel => pixel.id === pixelId);
      if (!initialized) {
        if (Date.now() < pending.deadline) this.#metaTimer = window.setTimeout(() => this.#tryMetaSearch(), 500);
        return;
      }
      this.#lastMetaQuery = pending.key;
      this.#pendingMetaSearch = null;
      const eventId = 'ac-search-' + crypto.randomUUID();
      fbq('trackSingle', pixelId, 'Search', { search_string: pending.query, content_type: 'product' }, { eventID: eventId });
      if (new URLSearchParams(window.location.search).has('ac_search_debug')) {
        console.info('[AC Search]', { status: 'queued', query: pending.query, pixelId, eventId });
      }
    } catch (error) {
      if (new URLSearchParams(window.location.search).has('ac_search_debug')) console.warn('[AC Search]', error);
    }
  }

  #render(html) {
    // Template parsing is inert: card images start loading only when inserted.
    const template = document.createElement('template');
    template.innerHTML = html;
    const searchResponse = template.content.querySelector('[data-ac-search-response]');
    if (!searchResponse) throw new Error('Search response is missing its root element');
    const announcement = searchResponse.querySelector('[data-ac-search-announcement]')?.textContent?.trim() || '';
    this.#resetScroll();
    this.#results.replaceChildren(...searchResponse.childNodes);
    this.#results.classList.add('is-query-results');
    this.#hidePopular();
    this.#announce(announcement);
  }

  #renderError(query) {
    const wrapper = document.createElement('div');
    const heading = document.createElement('h3');
    const copy = document.createElement('p');
    const link = document.createElement('a');
    const fallbackUrl = new URL(this.dataset.searchUrl || '/search', window.location.origin);
    fallbackUrl.searchParams.set('q', query);
    fallbackUrl.searchParams.set('type', 'product');
    fallbackUrl.searchParams.set('options[prefix]', 'last');

    wrapper.className = 'ac-search-overlay__empty';
    heading.textContent = this.dataset.unavailableLabel || 'Search is unavailable.';
    copy.textContent = this.dataset.unavailableCopy || '';
    link.className = 'ac-search-overlay__fallback-link ac-action ac-action--marker';
    link.href = fallbackUrl;
    link.textContent = this.dataset.fallbackLabel || 'View all results';
    wrapper.append(heading, copy, link);

    this.#resetScroll();
    this.#results.replaceChildren(wrapper);
    this.#results.classList.add('is-query-results');
    this.#hidePopular();
    this.#announce(heading.textContent);
  }

  #showPopular() {
    this.#query = '';
    this.#setLoading(false);
    if (!this.#results.childElementCount && !this.#popular?.hidden) return;
    this.#resetScroll();
    this.#results.classList.remove('is-query-results');
    this.#results.replaceChildren();
    this.#popular?.removeAttribute('hidden');
    this.#announce('');
    this.#refreshRail();
  }

  #reset() {
    this.#stopRail();
    this.#cancelPendingRequest();
    this.#form?.reset();
    if (this.#input) this.#input.value = '';
    this.#showPopular();
  }

  #cancelPendingRequest() {
    window.clearTimeout(this.#metaTimer);
    this.#pendingMetaSearch = null;
    window.clearTimeout(this.#timer);
    this.#requestController?.abort();
    if (this.#requestController) this.#query = '';
    this.#requestController = null;
    this.#generation += 1;
    this.#setLoading(false);
    this.#announce('');
  }

  #setLoading(isLoading) {
    this.#results?.setAttribute('aria-busy', String(isLoading));
    this.toggleAttribute('data-loading', isLoading);
  }

  #resetScroll() {
    if (this.#scroll) this.#scroll.scrollTop = 0;
    const viewport = this.#popular?.querySelector('[data-ac-product-list-viewport]');
    if (viewport) viewport.scrollLeft = 0;
  }

  #announce(message) {
    cancelAnimationFrame(this.#announcementFrame);
    this.#announcementFrame = 0;
    if (!this.#status) return;
    this.#status.textContent = '';
    if (!message) return;
    this.#announcementFrame = requestAnimationFrame(() => {
      this.#announcementFrame = 0;
      if (!this.isConnected) return;
      this.#status.textContent = message;
    });
  }
}

if (!customElements.get('ac-search-overlay')) customElements.define('ac-search-overlay', AcSearchOverlay);
